/**
 * Wing Chun: catálogo de formas por nivel y rotación de la forma del día (WingChunForm/WingChunRotation.swift).
 * Reglas: la forma del día es la activa que hace más tiempo que no practicas (nunca practicada primero; empate por
 * nivel); si un día no practicas, mañana sugiere la misma. Cualquier forma de la rotación cumple el mínimo, y Siu Nim
 * Tau siempre (plan B). Solo la primera práctica válida del día cuenta como mínimo.
 */

export interface WingChunForm { id: string; name: string; level: number; requirement: string }

export const WING_CHUN_FORMS: WingChunForm[] = [
  { id: 'siu_nim_tau', name: 'Siu Nim Tau', level: 1, requirement: 'En el sitio, sin material' },
  { id: 'cham_kiu', name: 'Cham Kiu', level: 2, requirement: 'Algo de espacio' },
  { id: 'biu_ji', name: 'Biu Ji', level: 3, requirement: 'Algo de espacio' },
  { id: 'mui_fa_jong', name: 'Mui Fa Jong', level: 4, requirement: 'Muñeco de madera' },
  { id: 'luk_dim_bun_gwan', name: 'Luk Dim Bun Gwan', level: 5, requirement: 'Bastón largo · mucho espacio' },
  { id: 'baat_jaam_do', name: 'Baat Jaam Do', level: 6, requirement: 'Cuchillos · espacio' },
];

export const MANDATORY_FORM = 'siu_nim_tau';
export const DEFAULT_ROTATION = ['siu_nim_tau', 'cham_kiu', 'biu_ji'];

export const formOf = (id: string) => WING_CHUN_FORMS.find(f => f.id === id);
export const formName = (id: string) => formOf(id)?.name ?? id;
export const formLevel = (id: string) => formOf(id)?.level ?? Number.MAX_SAFE_INTEGER;

const byLevel = (ids: string[]) => [...ids].sort((a, b) => formLevel(a) - formLevel(b));

export function parseRotation(raw: string | null | undefined): string[] {
  const ids = (raw ?? '').split(',').map(s => s.trim()).filter(id => formOf(id));
  return ids.length ? byLevel([...new Set(ids)]) : [...DEFAULT_ROTATION];
}

export const serializeRotation = (ids: string[]) => byLevel([...new Set(ids)]).join(',');

export const isEligibleForMinimum = (form: string, rotation: readonly string[]) => form === MANDATORY_FORM || rotation.includes(form);

export const eligibleForms = (rotation: readonly string[]) => byLevel([...new Set([...rotation, MANDATORY_FORM])]);

/** Forma activa practicada hace más tiempo. `lastPracticed`: forma → instante ISO de su última práctica. */
export function suggestedForm(rotation: readonly string[], lastPracticed: Record<string, string>): string {
  const candidates = rotation.length ? [...rotation] : [MANDATORY_FORM];
  return candidates.sort((a, b) => {
    const da = lastPracticed[a], db = lastPracticed[b];
    if (!da && !db) return formLevel(a) - formLevel(b);
    if (!da) return -1;
    if (!db) return 1;
    return da === db ? formLevel(a) - formLevel(b) : da < db ? -1 : 1;
  })[0]!;
}

export interface PracticeLike { form: string; date: string; completedAt: string; durationSeconds: number | null; isDailyMinimum: boolean }

export function lastPracticedByForm(practices: readonly PracticeLike[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of practices) if (p.completedAt && (!out[p.form] || out[p.form]! < p.completedAt)) out[p.form] = p.completedAt;
  return out;
}

/** Media de las prácticas cronometradas de una forma (segundos), o null. */
export function averageDuration(practices: readonly PracticeLike[], form: string): number | null {
  const timed = practices.filter(p => p.form === form && (p.durationSeconds ?? 0) > 0).map(p => p.durationSeconds!);
  return timed.length ? Math.round(timed.reduce((a, b) => a + b, 0) / timed.length) : null;
}

/** Días con el mínimo cumplido. */
export const minimumDays = (practices: readonly PracticeLike[]) => new Set(practices.filter(p => p.isDailyMinimum).map(p => p.date));
